import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, FormBuilder } from '@angular/forms';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import RZLib from 'src/app/core/backend-adapter/db2/RZ-lib.helpers';
import { ProgressComponent } from 'src/app/core/feature-modules/progress/progress.component';
import { ABPService } from 'src/app/abp-management/abp.service';
import { ABPPhotoConfigService } from 'src/app/abp-management/services/abp-photo-config-service/abp-photo-config-service';
import { SuccessComponent } from '../success/success.component';
import { SocketService } from 'src/app/core/backend-adapter/socket.service';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';

export interface IEventSaveObject {
  account_id: number;
  summary: string;
  languageCode: string;
  topicType: string;
  sourceURL: string;
  location_ids: any[] | string;
  scheduleTime: any;
  eventStartDate: any;
  eventEndDate: any;
  eventTitle: string;
  scheduleTimeZone: number;
  ctaActionType: string;
  eventStartTime?: any;
  eventEndTime?: any;
  ctaURL: string;
  _status: string;
  _id?: number;
}

@Component({
  selector: 'app-event-post',
  templateUrl: './event-post.component.html',
  styleUrls: ['./event-post.component.scss'],
})
export class EventPostComponent implements OnInit {
  @Input() rowData;
  @Input() type: string | undefined;

  stepsLabels = ['Settings', 'Photo', 'Content', 'Submit'];
  activeIndex = 0;
  /** True while the submit API request is in progress. */
  submitLoading = false;
  eventForm = new FormGroup({
    upload: new FormGroup({
      imageUrl: new FormControl(''),
    }),
    postDetails: new FormGroup({
      postDescription: new FormControl('', Validators.required),
      isAddTime: new FormControl(false),
      startDate: new FormControl('', Validators.required),
      endDate: new FormControl('', Validators.required),
      startTime: new FormControl(''),
      endTime: new FormControl(''),
      title: new FormControl('', Validators.required),
      addButton: new FormControl(''),
      ctaURL: new FormControl(
        '',
        Validators.pattern(
          /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#%[\]{}@!\$&'\(\)\*\+,;=]*(\{\{[A-Za-z0-9.]+\}\}|[\w\-\._~:/?#[\]{}@!\$&'\(\)\*\+,;=])*\/?$/
        )
      ),
      languageCode: new FormControl('', Validators.required),
      languageContents: new FormArray([]), // FormArray for dynamic language/heading/body pairs
      imageLanguages: new FormArray([]),   // 👈 TOP STEP
      contentLanguages: new FormArray([]), // 👈 NEXT STEP
    }),
    schedule: new FormGroup({
      checked: new FormControl(false),
      date: new FormControl(''),
      time: new FormControl(''),
      timeZone: new FormControl(''),
      location: new FormControl([]),
      isSelectedAll: new FormControl(''),
    }),
  });
  uploadGroup: FormGroup;
  postInfoGroup: FormGroup;
  scheduleGroup: FormGroup;
  formGroupName = 'upload';
  
  /**
   * Get the imageUrl form control for binding to the image upload component
   */
  get imageUrlControl(): FormControl {
    return this.uploadGroup?.get('imageUrl') as FormControl;
  }
  saveObj;
  closeAbpUtmFormEvent = new EventEmitter<void>();
  preSelectedLocation: any[] = [];

    languages = [
    { value: 'en-CA', label: 'English(Canada)' },
    { value: 'fr-CA', label: 'French' },
    { value: 'en-US', label: 'English(USA)' },
  ];
  /**
   * @description To display validations rules to upload an Image.
   */
  public photoRequirements: string[] = [];
  /**
   * @description Validation rules Title.
   */
  public photoRequirementsTitle: string;

  private readonly JWT_SECRET = '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a';
  private readonly API_BASE_URL = 'https://io.renderseo.com';
  private readonly IMAGE_UPLOAD_ENDPOINT = '/media/image/upload?bucket=apple-showcasecreative-images';
  /** Selected account id (same as listings: from getSelectedAccount$()). */
  private currentAccountId: number;
  /** Current user login id (same as listings: from getCurrentUser$()). */
  private userLoginId: number;

  isImageUploading: boolean = false;

  constructor(
    public dynamicDialogConfig: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    public dialogService: DialogService,
    private abpService: ABPService,
    private abpPhotoConfigService: ABPPhotoConfigService,
    private socketService: SocketService,
    private fb: FormBuilder,
    private http: HttpClient,
    private sessionService: SessionService,
    private notifyService: NotifyService,
  ) {}
  title = 'Youtube';

  /**
   * Handle image selection from the image upload component
   */
  onImageSelected(event: { file: File; base64: string; preview: string }): void {
    console.log('Image selected:', event.file.name);
    // Upload the image to the API and get the URL
    this.uploadImageToAPI(event.base64, event.file.name);
  }

  /**
   * Handle image cleared from the image upload component
   */
  onImageCleared(): void {
    // Image is already cleared in the form control via ControlValueAccessor
    // This method can be used for any additional logic if needed
    console.log('Image cleared');
    this.isImageUploading = false;
  }

  /**
   * Upload image to the media API and update form control with returned URL
   */
  private uploadImageToAPI(base64Image: string, filename: string): void {
    if (!base64Image || !filename) {
      console.error('Missing image data or filename');
      this.notifyService.error('Failed to upload image: Missing image data');
      return;
    }

    // Use account and user ids the same way as listings
    const userId = this.userLoginId;
    const accountId = this.currentAccountId;

    if (!userId || !accountId) {
      console.error('User ID or Account ID not available for image upload', { userId, accountId });
      this.notifyService.error('Failed to upload image: Authentication required');
      return;
    }

    this.isImageUploading = true;

    // Extract the actual image format from base64 data URL
    const mimeMatch = base64Image.match(/data:image\/([^;]+)/);
    const actualFormat = mimeMatch ? mimeMatch[1] : 'jpeg';
    
    // Map MIME type to file extension
    let extension: string;
    if (actualFormat === 'jpeg' || actualFormat === 'jpg') {
      extension = 'jpg';
    } else if (actualFormat === 'png') {
      extension = 'png';
    } else if (actualFormat === 'heic' || actualFormat === 'heif') {
      extension = 'jpg'; // HEIC/HEIF are converted to JPEG
    } else {
      extension = actualFormat;
    }

    // Ensure filename has the correct extension matching the actual image format
    let baseFilename = filename;
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex > 0) {
      // Remove the original extension
      baseFilename = filename.substring(0, lastDotIndex);
    }
    
    // Add the correct extension based on actual image format
    const finalFilename = `${baseFilename}.${extension}`;

    // Generate JWT token for authentication
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000, // ~18 seconds
    };

    const jwtToken = generateJWTToken(payloadObj, this.JWT_SECRET);

    // Prepare request payload
    const payload = {
      image: base64Image,
      filename: finalFilename
    };

    // Always use full API URL so the request goes directly to the media API (https://io.renderseo.com)
    const apiUrl = `${this.API_BASE_URL}${this.IMAGE_UPLOAD_ENDPOINT}`;

    console.log('=== UPLOADING IMAGE TO API ===');
    console.log('API URL:', apiUrl);
    console.log('Filename:', finalFilename);
    console.log('Image data length:', base64Image.length);

    // Make API call with JWT authentication
    const headers = new HttpHeaders()
      .set('Authorization', `${jwtToken}`)
      .set('Content-Type', 'application/json');

    this.http.post<any>(apiUrl, payload, { headers }).subscribe(
      (response) => {
        console.log('=== IMAGE UPLOAD SUCCESS ===');
        console.log('Response:', response);
        
        // Extract URL from response
        // The response structure may vary, so we'll handle different possible formats
        let imageUrl: string = '';
        
        if (typeof response === 'string') {
          // If response is a direct URL string
          imageUrl = response;
        } else if (response?.url) {
          // If response has a url property
          imageUrl = response.url;
        } else if (response?.imageUrl) {
          // If response has an imageUrl property
          imageUrl = response.imageUrl;
        } else if (response?.data?.url) {
          // If response has nested data.url
          imageUrl = response.data.url;
        } else {
          // Try to find any URL-like string in the response
          const responseStr = JSON.stringify(response);
          const urlMatch = responseStr.match(/https?:\/\/[^\s"']+/);
          if (urlMatch) {
            imageUrl = urlMatch[0];
          }
        }

        if (imageUrl) {
          // Update form control with the returned URL
          this.uploadGroup.patchValue({
            imageUrl: imageUrl
          });
          console.log('Image URL set in form control:', imageUrl);
          this.notifyService.success('Image uploaded successfully');
        } else {
          console.error('No URL found in response:', response);
          this.notifyService.error('Failed to upload image: Invalid response format');
        }
        
        this.isImageUploading = false;
        console.log('=== END IMAGE UPLOAD ===');
      },
      (error) => {
        console.error('=== IMAGE UPLOAD ERROR ===');
        console.error('Error:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);
        console.error('Error body:', error?.error);
        
        const errorMessage = error?.error?.message || error?.message || 'Failed to upload image. Please try again.';
        this.notifyService.error(errorMessage);
        this.isImageUploading = false;
        
        // Clear the form control on error
        this.uploadGroup.patchValue({
          imageUrl: ''
        });
        
        console.log('=== END IMAGE UPLOAD ERROR ===');
      }
    );
  }




  ngOnInit(): void {
    // Fetch account and user the same way as listings.component
    this.sessionService.getSelectedAccount$().subscribe((account) => {
      this.currentAccountId = account?._id;
    });
    this.sessionService.getCurrentUser$().subscribe((user) => {
      this.userLoginId = user?.login?._id;
    });

    this.postInfoGroup = this.eventForm.controls.postDetails as FormGroup;
    this.scheduleGroup = this.eventForm.controls.schedule as FormGroup;
    this.uploadGroup = this.eventForm.controls.upload as FormGroup;
    this.setPhotoValidationRules();

    if (this.rowData) {
      this.setInitialFormValues();
    }
    if (this.languageContents.length === 0) {
    this.addLanguageContent(); 
  }

  if (this.languageContents.length === 0) {
    this.onAddLanguageClick(); // 👈 uses the NEW function
  }

  if (this.imageLanguages.length === 0) {
    this.addImageLanguage(); // top step
  }

  if (this.contentLanguages.length === 0) {
    this.addContentLanguage(); // next step
  }
  }

  /**
   * @description Method set's validation rules on Add Photo screen for Media type PHOTO.
   * @returns void
   */
  private setPhotoValidationRules(): void {
    this.photoRequirements = this.abpPhotoConfigService.setABPMediaRequirements('PHOTO').description;
    this.photoRequirementsTitle = this.abpPhotoConfigService.setABPMediaRequirements('PHOTO').title;
  }

  private setInitialFormValues() {
    this.uploadGroup.setValue({
      imageUrl: this.rowData?.internalURL || this.rowData?.sourceURL,
    });

    const today = new Date();
    this.postInfoGroup.patchValue({
      postDescription: this.rowData?.summary,
      isAddTime: this.rowData?.eventStartTime ? true : false,
      startDate: new Date(this.rowData?.eventStartDate._year_, this.rowData?.eventStartDate._month_ - 1, this.rowData?.eventStartDate._day_),
      endDate: new Date(this.rowData?.eventEndDate._year_, this.rowData?.eventEndDate._month_ - 1, this.rowData?.eventEndDate._day_),
      startTime: this.rowData?.eventStartTime
        ? new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            this.rowData.eventStartTime.hour,
            this.rowData.eventStartTime.minute,
            0
          )
        : new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0),
      endTime: this.rowData?.eventEndTime
        ? new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            this.rowData.eventEndTime.hour,
            this.rowData.eventEndTime.minute,
            0
          )
        : new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59),
      title: this.rowData?.eventTitle,
      addButton: this.rowData?.ctaActionType || '',
      ctaURL: this.rowData?.ctaURL,
      languageCode: this.abpService.preparePostLanguage(this.rowData?.languageCode),
    });

    // Set schedule Date, Time and Timezone.
    const scheduledTime = this.rowData.scheduleTime !== null ?
      (new Date(new Date().getDate(), new Date().getMonth() + 1, new Date().getFullYear(), this.rowData.scheduleTime?._hour_, this.rowData.scheduleTime?._minute_, this.rowData.scheduleTime?._second_)) : '';
    const scheduledDate = this.rowData.scheduleTime !== null ?
      (new Date(`${this.rowData.scheduleTime?._month_}/${this.rowData.scheduleTime?._day_}/${this.rowData.scheduleTime?._year_}`)) : '';
    this.scheduleGroup.patchValue({
      date: scheduledDate,
      time: scheduledTime,
      timeZone: this.rowData?.scheduleTimeZone,
    });

    // Add a new control if Post status === A (Active).
    if (this.rowData._status === 'A') {
      this.scheduleGroup.addControl('postStatus', new FormControl(true));
    }


    this.socketService.sendRequest('get-localpost-detail-coll', { 'detailLevel': 3, 'where': ['-and', `post_id=${this.rowData._id}`] })
    .then(res => {
      res['collection'].forEach(l => {
        this.preSelectedLocation.push({
          _id: l.location_id,
          storeCode: l.storeCode,
          businessName: l.businessName,
          city: l.city,
          state: l.state,
          disabled: true
        });
      });

      // Added a new control to store pre-selected locations.
      this.scheduleGroup.addControl('preSelectedLocation', new FormControl(
        [...this.preSelectedLocation]
      ));

    })

  }

  // private prepareEventSaveObject(postDetailsGroup, scheduleGroup, uploadGroup) {
  //   const tempObj: IEventSaveObject = {
  //     account_id: 0,
  //     summary: '',
  //     languageCode: '',
  //     topicType: '',
  //     sourceURL: '',
  //     location_ids: [],
  //     scheduleTime: new RZLib.Timestamp().setNow(),
  //     eventStartDate: new RZLib.Timestamp().setNow(),
  //     eventEndDate: new RZLib.Timestamp().setNow(),
  //     eventTitle: '',
  //     scheduleTimeZone: 0,
  //     ctaActionType: '',
  //     ctaURL: '',
  //     _status: 'AP',
  //   };

  //   tempObj.account_id = this.abpService.account_id;
  //   tempObj.summary = postDetailsGroup.postDescription;
  //   tempObj.languageCode = postDetailsGroup.languageCode;
  //   tempObj.topicType = 'EVENT';
  //   const urlFieldName = new RegExp(/^https:\/\/rseo-platform(-dev)?.s3.amazonaws.com\/media\/.+/i).test(
  //     uploadGroup.imageUrl
  //   )
  //     ? 'internalURL'
  //     : 'sourceURL';
  //   tempObj[urlFieldName] = uploadGroup.imageUrl;

  //   tempObj.eventStartDate = this.abpService.prepareDate(postDetailsGroup.startDate, tempObj.eventStartDate);
  //   tempObj.eventEndDate = this.abpService.prepareDate(postDetailsGroup.endDate, tempObj.eventEndDate);

  //   tempObj.eventTitle = postDetailsGroup.title;
  //   tempObj.ctaActionType = postDetailsGroup.addButton.toUpperCase();
  //   tempObj.ctaURL = postDetailsGroup.ctaURL || '';

  //   tempObj.location_ids = scheduleGroup.isSelectedAll
  //     ? '*'
  //     : scheduleGroup.location?.length > 0
  //     ? this.abpService.mapAllLocations(scheduleGroup.location, this.preSelectedLocation)
  //     : null;
  //   tempObj.scheduleTime = scheduleGroup.checked ? this.abpService.prepareDate(scheduleGroup.date, tempObj.scheduleTime, scheduleGroup.time) : '';
  //   tempObj.scheduleTimeZone = scheduleGroup.checked ? scheduleGroup.timeZone : null;

  //   if (postDetailsGroup.startTime && postDetailsGroup.endTime) {
  //     tempObj.eventStartTime = new RZLib.Timestamp().setNow();
  //     tempObj.eventEndTime = new RZLib.Timestamp().setNow();
  //     tempObj.eventStartTime = this.abpService.prepareTime(tempObj.eventStartTime, postDetailsGroup.startTime);
  //     tempObj.eventEndTime = this.abpService.prepareTime(tempObj.eventEndTime, postDetailsGroup.endTime);
  //   }

  //   // Edit mode
  //   if (this.rowData) {
  //     tempObj._id = this.rowData._id;
  //   }

  //   return tempObj;
  // }

  // ... existing code ...

  private prepareEventSaveObject(postDetailsGroup, scheduleGroup, uploadGroup) {
    console.log('=== PREPARING EVENT SAVE OBJECT ===');
    console.log('Raw Form Data - Post Details:', postDetailsGroup);
    console.log('Raw Form Data - Schedule:', scheduleGroup);
    console.log('Raw Form Data - Upload:', uploadGroup);
    console.log('Call to Action (addButton) value:', postDetailsGroup.addButton);
    
    // Transform to the new JSON format
    const saveObj: any = {
      callToAction: postDetailsGroup.addButton ? postDetailsGroup.addButton.toUpperCase() : '',
      appIds: [],
      contents: [],
      photo: {
        s3Url: uploadGroup.imageUrl || '',
        captions: []
      },
      locations: scheduleGroup.isSelectedAll 
        ? [] // Handle "all locations" case as needed
        : scheduleGroup.location?.length > 0
        ? this.abpService.mapAllLocations(scheduleGroup.location, this.preSelectedLocation)
        : [],
      startDate: this.formatDateForAPI(postDetailsGroup.startDate),
      endDate: this.formatDateForAPI(postDetailsGroup.endDate)
    };
    
    console.log('Initial Save Object:', JSON.stringify(saveObj, null, 2));

    // Map contentLanguages FormArray to contents array
    // Group by placement (HEADLINE and BODY)
    const headlineContents = [];
    const bodyContents = [];

    console.log('Content Languages from Form:', postDetailsGroup.contentLanguages);
    
    if (postDetailsGroup.contentLanguages && postDetailsGroup.contentLanguages.length > 0) {
      postDetailsGroup.contentLanguages.forEach((contentLang: any, index: number) => {
        console.log(`Processing Content Language ${index + 1}:`, contentLang);
        const locale = this.mapLocaleCode(contentLang.languageCode);
        console.log(`Mapped locale: ${contentLang.languageCode} -> ${locale}`);
        
        if (contentLang.heading) {
          headlineContents.push({
            locale: locale,
            text: contentLang.heading
          });
          console.log(`Added to HEADLINE:`, { locale, text: contentLang.heading });
        }
        
        if (contentLang.bodyText) {
          bodyContents.push({
            locale: locale,
            text: contentLang.bodyText
          });
          console.log(`Added to BODY:`, { locale, text: contentLang.bodyText });
        }
      });
    }

    // Add HEADLINE content if exists
    if (headlineContents.length > 0) {
      saveObj.contents.push({
        placement: 'HEADLINE',
        descriptions: headlineContents
      });
      console.log('HEADLINE contents added:', headlineContents);
    }

    // Add BODY content if exists
    if (bodyContents.length > 0) {
      saveObj.contents.push({
        placement: 'BODY',
        descriptions: bodyContents
      });
      console.log('BODY contents added:', bodyContents);
    }

    // Map imageLanguages FormArray to photo.captions
    console.log('Image Languages from Form:', postDetailsGroup.imageLanguages);
    
    if (postDetailsGroup.imageLanguages && postDetailsGroup.imageLanguages.length > 0) {
      postDetailsGroup.imageLanguages.forEach((imageLang: any, index: number) => {
        console.log(`Processing Image Language ${index + 1}:`, imageLang);
        const locale = this.mapLocaleCode(imageLang.languageCode);
        console.log(`Mapped locale: ${imageLang.languageCode} -> ${locale}`);
        saveObj.photo.captions.push({
          locale: locale,
          altText: imageLang.altText || ''
        });
        console.log(`Added photo caption:`, { locale, altText: imageLang.altText });
      });
    }

    // Edit mode - add _id if editing
    if (this.rowData) {
      saveObj._id = this.rowData._id;
      console.log('Edit mode - Added _id:', this.rowData._id);
    }

    console.log('=== FINAL SAVE OBJECT ===');
    console.log('Complete Save Object (Formatted):', JSON.stringify(saveObj, null, 2));
    console.log('Complete Save Object (Object):', saveObj);
    console.log('=== END PREPARING EVENT SAVE OBJECT ===');

    return saveObj;
  }

  /**
   * Format date to YYYY-MM-DD format
   */
  private formatDateForAPI(date: Date | string): string {
    if (!date) return '';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  /**
   * Map locale codes (e.g., en-CA -> en-CA, fr-CA -> fr-CA, en-US -> en-US)
   */
  private mapLocaleCode(languageCode: string): string {
    if (!languageCode) return 'en-US';
    
    // Keep original locale codes - no mapping needed
    // The API accepts: en-US, en-CA, fr-CA, etc.
    const validLocales = ['en-US', 'en-CA', 'fr-CA', 'es-US'];
    
    // If it's a valid locale, return it as-is
    if (validLocales.includes(languageCode)) {
      return languageCode;
    }
    
    // Default fallback
    return 'en-US';
  }

  /**
   * Call the Apple Showcase Creative API
   */
  private saveShowcaseCreative(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      // Use account and user ids the same way as listings
      const userId = this.userLoginId;
      const accountId = this.currentAccountId;

      if (!userId || !accountId) {
        console.error('User ID or Account ID not available for API call', { userId, accountId });
        reject(new Error('User ID or Account ID not available'));
        return;
      }

      // Generate JWT token
      const payloadObj = {
        iss: 'https://platform.renderseo.com',
        sub: userId,
        account_id: accountId,
        iat: new Date().getTime(),
        exp: new Date().getTime() + 3 * 6000, // ~18 seconds
      };

      const jwtToken = generateJWTToken(payloadObj, this.JWT_SECRET);
      const headers = new HttpHeaders()
        .set('Authorization', `${jwtToken}`)
        .set('Content-Type', 'application/json');

      // Use proxy URL if running on localhost (dev), otherwise use full URL
      // IMPORTANT: For proxy to work, you MUST restart the dev server after adding proxy.conf.json
      // To use proxy: Change this to '/apple/saveshowcasecreative' and restart: npm run ng:start
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = isLocalhost 
        ? '/apple/saveshowcasecreative'  // Use proxy in development
        : `${this.API_BASE_URL}/apple/saveshowcasecreative`;  // Use full URL in production

      console.log('=== API CALL DETAILS ===');
      console.log('API URL:', apiUrl);
      console.log('Request Method: POST');
      console.log('Request Headers:', {
        'Authorization': 'JWT Token (hidden)',
        'Content-Type': 'application/json'
      });
      console.log('Request Payload:', JSON.stringify(data, null, 2));
      console.log('Request Payload (Object):', data);

      // Make API call
      this.http.post<any>(apiUrl, data, { headers }).subscribe(
        (response) => {
          console.log('=== API RESPONSE (SUCCESS) ===');
          console.log('Response Status: SUCCESS');
          console.log('Response Data (Formatted):', JSON.stringify(response, null, 2));
          console.log('Response Data (Object):', response);
          console.log('=== END API RESPONSE ===');
          resolve(response);
        },
        (error) => {
          console.error('=== API RESPONSE (ERROR) ===');
          console.error('Response Status: ERROR');
          console.error('Error Object:', error);
          console.error('Error Message:', error?.message || 'Unknown error');
          console.error('Error Status:', error?.status || 'N/A');
          console.error('Error Body:', error?.error || 'N/A');
          console.error('=== END API ERROR ===');
          reject(error);
        }
      );
    });
  }

// ... existing code ...

  public goNext(progress: ProgressComponent): void {
    if (this.closeAbpUtmFormEvent) this.closeAbpUtmFormEvent.emit();
    if (this.activeIndex === 3) {
      this.submitLoading = true;
    }
    progress.next();
  }

  onStateChange(event) {
    if (event.status == 'completed') {
      console.log('=== FORM SUBMISSION STARTED ===');
      console.log('Form Status: COMPLETED');
      
      // Prepare the data in the new JSON format
      this.saveObj = this.prepareEventSaveObject(
        this.postInfoGroup.value,
        this.scheduleGroup.value,
        this.uploadGroup.value
      );

      console.log('=== CALLING API ===');
      console.log('Data being sent to API:', JSON.stringify(this.saveObj, null, 2));
      console.log('API Endpoint: https://io.renderseo.com/apple/saveshowcasecreative');
      console.log('Request Method: POST');

      // Call the Apple Showcase Creative API
      this.saveShowcaseCreative(this.saveObj)
        .then((response) => {
          this.submitLoading = false;
          console.log('=== FORM SUBMISSION SUCCESS ===');
          console.log('API Response received:', response);
          console.log('Saving to local post...');
          
          // Also save to local post (if needed)
          this.abpService.updateLocalPost(this.saveObj);
          console.log('Local post saved successfully');
          console.log('=== END FORM SUBMISSION ===');
          
          // Close dialog and show success
          this.ref.close();
          this.dialogService.open(SuccessComponent, {
            width: '20%',
          });
        })
        .catch((error) => {
          this.submitLoading = false;
          console.error('=== FORM SUBMISSION FAILED ===');
          console.error('Error details:', error);
          console.error('Save object that failed:', this.saveObj);
          console.error('=== END FORM SUBMISSION ERROR ===');
          // You might want to show an error message to the user here
          // For example, using a notification service
          alert('Error saving showcase creative. Please try again.');
        });
    }
    this.activeIndex = event.activeIndex;
    switch (this.activeIndex) {
      case 0: {
        this.formGroupName = 'upload';
        break;
      }
      case 1: {
        this.formGroupName = 'postDetails';
        break;
      }
      case 2: {
        this.formGroupName = 'schedule';
        break;
      }
    }
  }

  closeDialog() {
    this.ref.close();
  }

  imageChanged(url) {
    this.eventForm.patchValue({ upload: { imageUrl: url[0] } });
  }

  public get checkDateAndLocStatus(): boolean {
    const isChecked = this.scheduleGroup.controls;
    let dateSelection: boolean;
    if (this.formGroupName === 'schedule') {
      if (!isChecked['checked'].value) {
        return (dateSelection =
          !isChecked['checked'].value && (isChecked['location'].value.length || isChecked['isSelectedAll'].value || isChecked['preSelectedLocation']?.value?.length)
            ? false
            : true);
      } else {
        return (dateSelection =
          isChecked['date'].value &&
          isChecked['time'].value &&
          isChecked['timeZone'].value &&
          (isChecked['location'].value.length || isChecked['isSelectedAll'].value || isChecked['preSelectedLocation']?.value?.length)
            ? false
            : true);
      }
    } else {
      false;
    }
  }

  public get checkAddButtonStatus(): boolean {
    let status: boolean = false;
    if (this.postInfoGroup.controls['addButton'].value && this.postInfoGroup.controls['addButton'].value !== 'CALL') {
      if (!this.postInfoGroup.controls['ctaURL'].value) {
        status = true;
      } else {
        status = false;
      }
    }
    return status;
  }

  get imageLanguages(): FormArray {
  return this.postInfoGroup.get('imageLanguages') as FormArray;
}

get contentLanguages(): FormArray {
  return this.postInfoGroup.get('contentLanguages') as FormArray;
}

addImageLanguage(): void {
  this.imageLanguages.push(   
    this.fb.group({
      altText: new FormControl('', Validators.required),
      languageCode: new FormControl('', Validators.required),
    })
  );
}
removeImageLanguage(index: number): void {
    // Prevent removing if there's only one language block (at least one is required)
    if (this.imageLanguages.length > 1) {
      this.imageLanguages.removeAt(index);
    }
  }

    onImageLanguageClick(): void {
  const imageLanguageGroup = this.fb.group({
    altText: new FormControl('', Validators.required),
    languageCode: new FormControl('', Validators.required),
  });

  this.imageLanguages.push(imageLanguageGroup);
}

addContentLanguage(): void {
  this.contentLanguages.push(
    this.fb.group({
      heading: new FormControl('', Validators.required),
      bodyText: new FormControl('', Validators.required),
      languageCode: new FormControl('', Validators.required),
    })
  );
}
removeContentLanguage(index: number): void {
    // Prevent removing if there's only one language block (at least one is required)
    if (this.contentLanguages.length > 1) {
      this.contentLanguages.removeAt(index);
    }
  }

    onContentLanguageClick(): void {
  const contentLanguageGroup = this.fb.group({
    altText: new FormControl('', Validators.required),
    languageCode: new FormControl('', Validators.required),
  });

  this.contentLanguages.push(contentLanguageGroup);
    }

  /**
   * Get the languageContents FormArray
   */
  get languageContents(): FormArray {
    return this.postInfoGroup.get('languageContents') as FormArray;
  }

  /**
   * Add a new language content pair (Heading and Body Text)
   */
  addLanguageContent(): void {
    const languageContentGroup = this.fb.group({
      heading: new FormControl('', Validators.required),
      bodyText: new FormControl('', Validators.required),
    });
    this.languageContents.push(languageContentGroup);
  }
  /**
   * Remove a language content pair at the given index
   */
  removeLanguageContent(index: number): void {
    this.languageContents.removeAt(index);
  }

    onAddLanguageClick(): void {
  const languageGroup = this.fb.group({
    altText: new FormControl('', Validators.required),
    languageCode: new FormControl('', Validators.required),
  });

  this.languageContents.push(languageGroup);
}

  /**
   * Check if the current step is valid
   * Returns true if all required fields for the current step are filled
   */
  public get isCurrentStepValid(): boolean {
    switch (this.activeIndex) {
      case 0: // Settings step
        return this.isSettingsStepValid();
      case 1: // Photo step
        return this.isPhotoStepValid();
      case 2: // Content step
        return this.isContentStepValid();
      case 3: // Submit step
        return false; // No NEXT button on final step
      default:
        return false;
    }
  }

  /**
   * Validate Settings step (Step 0)
   * Requires: location, CTA, start date, end date
   */
  private isSettingsStepValid(): boolean {
    // Check location (either selected locations, all selected, or pre-selected)
    const hasLocation = 
      (this.scheduleGroup.get('location')?.value?.length > 0) ||
      (this.scheduleGroup.get('isSelectedAll')?.value === true) ||
      (this.scheduleGroup.get('preSelectedLocation')?.value?.length > 0);

    // Check CTA
    const hasCTA = !!this.postInfoGroup.get('addButton')?.value;

    // Check dates
    const hasStartDate = !!this.postInfoGroup.get('startDate')?.value;
    const hasEndDate = !!this.postInfoGroup.get('endDate')?.value;

    // If CTA is set and not 'CALL', check if CTA URL is valid
    const ctaValue = this.postInfoGroup.get('addButton')?.value;
    const ctaURLValid = ctaValue === 'CALL' || 
      (ctaValue && this.postInfoGroup.get('ctaURL')?.valid);

    return hasLocation && hasCTA && hasStartDate && hasEndDate && ctaURLValid;
  }

  /**
   * Validate Photo step (Step 1)
   * Requires: image uploaded and all image languages valid
   */
  private isPhotoStepValid(): boolean {
    // Check if image is uploaded
    const hasImage = !!this.uploadGroup.get('imageUrl')?.value;

    // Check if all image languages are valid
    const imageLanguagesValid = this.imageLanguages.controls.every(
      (control) => control.valid
    );

    // Must have at least one image language
    const hasImageLanguages = this.imageLanguages.length > 0;

    return hasImage && imageLanguagesValid && hasImageLanguages;
  }

  /**
   * Validate Content step (Step 2)
   * Requires: all content languages valid
   */
  private isContentStepValid(): boolean {
    // Check if all content languages are valid
    const contentLanguagesValid = this.contentLanguages.controls.every(
      (control) => control.valid
    );

    // Must have at least one content language
    const hasContentLanguages = this.contentLanguages.length > 0;

    return contentLanguagesValid && hasContentLanguages;
  }

}
